interface UuidV7State {
  lastTimestampMs: number;
  sequence: bigint;
}

const state: UuidV7State = {
  lastTimestampMs: -1,
  sequence: 0n,
};

function getRandomBytes(count: number): Uint8Array {
  const bytes = new Uint8Array(count);
  if (typeof globalThis !== 'undefined' && globalThis.crypto && typeof globalThis.crypto.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < count; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return bytes;
}

function getRandomInt(min: number, max: number): number {
  const range = max - min;
  const bytes = getRandomBytes(4);
  const val = (bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3];
  return min + (Math.abs(val) % range);
}

/**
 * Generates a Monotonic RFC 9562 compliant UUIDv7 (timestamp-ordered 128-bit identifier).
 * Cross-platform compatible (Node.js, Browser, Web Workers, Electron Renderer).
 * Guaranteed to be strictly monotonic when generated on the same process.
 * Protects against clock rollback without generating smaller values.
 */
export function generateUUIDv7(nowMs: number = Date.now()): string {
  if (nowMs < 0 || nowMs >= 281474976710656) {
    // 2^48 - 1 (Max 48-bit timestamp)
    throw new Error(`Timestamp out of range for UUIDv7 48-bit allocation: ${nowMs}`);
  }

  let timestamp = nowMs;

  if (timestamp > state.lastTimestampMs) {
    state.lastTimestampMs = timestamp;
    state.sequence = BigInt(getRandomInt(0, 0x0fff)); // 12-bit random counter initialization
  } else if (timestamp === state.lastTimestampMs) {
    state.sequence = (state.sequence + 1n) & 0x0fffn;
    if (state.sequence === 0n) {
      // Sequence overflow in same millisecond -> advance logical timestamp
      state.lastTimestampMs += 1;
      timestamp = state.lastTimestampMs;
    }
  } else {
    // Clock moved backward -> preserve last timestamp and advance sequence
    state.sequence = (state.sequence + 1n) & 0x0fffn;
    if (state.sequence === 0n) {
      state.lastTimestampMs += 1;
    }
    timestamp = state.lastTimestampMs;
  }

  const timestampBytes = new Uint8Array(6);
  let time = timestamp;
  for (let i = 5; i >= 0; i--) {
    timestampBytes[i] = time & 0xff;
    time = Math.floor(time / 256);
  }

  const randBytes = getRandomBytes(10);

  // Set Version 7 (0x70) on high nibble of random field (bytes 6-7)
  const verSeqHigh = Number((state.sequence >> 8n) & 0x0fn) | 0x70;
  const seqLow = Number(state.sequence & 0xffn);

  // Set Variant 10xx (0x80) on byte 8
  const varRand = (randBytes[2] & 0x3f) | 0x80;

  const hexBytes = [
    timestampBytes[0],
    timestampBytes[1],
    timestampBytes[2],
    timestampBytes[3],
    timestampBytes[4],
    timestampBytes[5],
    verSeqHigh,
    seqLow,
    varRand,
    randBytes[3],
    randBytes[4],
    randBytes[5],
    randBytes[6],
    randBytes[7],
    randBytes[8],
    randBytes[9],
  ];

  const hex = hexBytes.map((b) => b.toString(16).padStart(2, '0')).join('');

  return [
    hex.substring(0, 8),
    hex.substring(8, 12),
    hex.substring(12, 16),
    hex.substring(16, 20),
    hex.substring(20, 32),
  ].join('-');
}

export function resetUuidV7State(): void {
  state.lastTimestampMs = -1;
  state.sequence = 0n;
}
